import type { SCFontItem } from 'types/fonts';
import { generateId } from 'utils/generate-id';
import { canonicalFamilyName, type ResolvedFont } from 'admin/components/common/AI/Chat/fonts/font-resolver';

import type { Story } from './graph/story';

export function registerResolvedFontsFromUsage(draftStory: Story, fonts: ResolvedFont[]): void {
	if (!fonts.length) return;
	draftStory.settings.fonts = registerResolvedFonts(draftStory.settings.fonts ?? {}, fonts);
}

export function registerResolvedFonts(
	existing: Record<string, SCFontItem>,
	fonts: ResolvedFont[]
): Record<string, SCFontItem> {
	const next = { ...existing };
	const registeredFamilies = new Set(Object.values(existing).map(font => canonicalFamilyName(font.fontFamily)));

	for (const font of fonts) {
		if (font.source === 'system' || font.source === 'story') continue;
		const family = canonicalFamilyName(font.family);
		if (!family || registeredFamilies.has(family)) continue;

		if (font.source === 'organization') {
			for (const face of font.faces ?? []) addPreservingExisting(next, face);
			if (font.faces?.length) registeredFamilies.add(family);
			continue;
		}

		if (font.source === 'google' && font.google) {
			const item = toStoryGoogleFont(font.google.family, font.google.variants);
			addPreservingExisting(next, item);
			registeredFamilies.add(family);
		}
	}

	return next;
}

function addPreservingExisting(draftFonts: Record<string, SCFontItem>, font: SCFontItem): void {
	if (!draftFonts[font.id]) {
		draftFonts[font.id] = font;
		return;
	}
	if (draftFonts[font.id] === font) return;

	let id = generateId(4);
	while (draftFonts[id]) id = generateId(4);
	draftFonts[id] = { ...font, id };
}

function toStoryGoogleFont(family: string, variants: string[]): SCFontItem {
	return {
		id: generateId(4),
		fontFamily: family,
		fontType: 'google',
		fileType: '-',
		size: 0,
		style: '-',
		weight: 0,
		url: `${family.replaceAll(' ', '+')}:${variants.join(',')}`,
		variants: [...variants],
	};
}
